import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";
import { PLATFORM_FEATURES, REGULATORY } from "@/data";

const PAGES = {
  platform: { kicker: "Platform", title: "One platform for the entire asset lifecycle", body: "AUVRA unifies issuance, compliance, custody and settlement into a single institutional rail — so you can bring real-world value on-chain without stitching together vendors.", points: PLATFORM_FEATURES },
  "tokenized-assets": { kicker: "Tokenized Assets", title: "Real value, made programmable", body: "Real estate, private credit, commodities and treasuries — fractionalized into compliant digital securities with enforceable legal rights and transparent on-chain records.", points: PLATFORM_FEATURES },
  "aur-token": { kicker: "AUR Token", title: "The utility layer of the AUVRA network", body: "AUR aligns incentives across issuers, investors and the protocol — powering governance, network rewards and access to premium infrastructure.", points: PLATFORM_FEATURES },
  investors: { kicker: "Investors", title: "Institutional access to private markets", body: "Verified accredited and institutional investors gain compliant access to curated tokenized opportunities with full transparency and instant settlement.", points: PLATFORM_FEATURES },
  partnerships: { kicker: "Partnerships", title: "Build on institutional infrastructure", body: "Banks, asset managers and fintechs integrate AUVRA to launch compliant tokenization products under their own brand.", points: PLATFORM_FEATURES },
  compliance: { kicker: "Compliance", title: "Compliant by construction", body: "Every issuance embeds jurisdiction-aware rules covering KYC/AML, transfer restrictions, accreditation and audit trails — enforced automatically.", points: REGULATORY },
  resources: { kicker: "Resources", title: "Documentation, research & insights", body: "Explore the AUVRA whitepaper, developer documentation and institutional research on the tokenized asset economy.", points: PLATFORM_FEATURES },
  about: { kicker: "About", title: "Institutional trust, engineered", body: "AUVRA is built by a team spanning capital markets, regulation and blockchain — united behind one uncompromising standard of trust.", points: PLATFORM_FEATURES, image: "/brand/team.jpg" },
  contact: { kicker: "Contact", title: "Talk to our institutional team", body: "Whether you're looking to invest, tokenize an asset or partner with AUVRA, our team is ready to help.", points: [], contact: true },
};

export default function InfoPage({ onCta }) {
  const { slug } = useParams();
  const page = PAGES[slug] || PAGES.platform;
  return (
    <>
      <Navbar onCta={onCta} />
      <main className="pt-40 pb-24">
        <div className="container-auvra">
          <Reveal><span className="kicker">{page.kicker}</span></Reveal>
          <Reveal i={1}>
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tighter mt-6 max-w-4xl leading-[1.05]">{page.title}</h1>
          </Reveal>
          <Reveal i={2}><p className="text-lg text-secondary-auvra mt-8 max-w-2xl leading-relaxed">{page.body}</p></Reveal>

          {page.image && (
            <Reveal i={3} className="mt-12">
              <img src={page.image} alt="AUVRA team" className="w-full rounded-2xl border border-white/10" />
            </Reveal>
          )}

          {page.points?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
              {page.points.map((p, i) => (
                <Reveal key={p.title} i={i}>
                  <div className="glass rounded-2xl p-8 h-full">
                    <p.icon className="w-6 h-6 text-auvra-blue mb-4" />
                    <h3 className="text-lg font-medium mb-2">{p.title}</h3>
                    <p className="text-secondary-auvra text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal className="mt-16 flex flex-col sm:flex-row gap-4">
            <button onClick={onCta} data-testid="info-cta" className="flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-7 py-3.5 rounded-full transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.5)]">
              Get Started <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/signup" className="border border-white/15 hover:border-white/40 text-white font-medium px-7 py-3.5 rounded-full transition-all hover:bg-white/5 text-center">
              Open Investor Account
            </Link>
          </Reveal>
        </div>
      </main>
      <Footer />
    </>
  );
}
